import { useEffect } from 'react'
import {
  useDateStoreActions,
  useDeliveryDateContainer,
  usePickupDateBag,
  usePickupDateContainer,
} from '@/stores/useDateStore'

import { useBasket } from '../(basket)'
import { DateInput } from '@/app/[locale]/(ui)/date-input'
import { toOsloDateString } from '@/lib/pickup-date'
import { useTranslations } from 'next-intl'

const parseStoredDate = (storedDate?: string) => {
  if (!storedDate) return undefined

  const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/
  if (dateOnlyRegex.test(storedDate)) {
    const [yearPart, monthPart, dayPart] = storedDate.split('-')
    const year = Number(yearPart)
    const month = Number(monthPart)
    const day = Number(dayPart)

    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
      return undefined
    }

    return new Date(year, month - 1, day, 12, 0, 0, 0)
  }

  const parsedDate = new Date(storedDate)
  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate
}

const isBeforeTodayInOslo = (storedDate: string | undefined, todayDate: string) => {
  const parsedDate = parseStoredDate(storedDate)
  if (!parsedDate) return true

  return toOsloDateString(parsedDate) < todayDate
}

export const PickupDateChooser = ({ setIsInErrorState }: any) => {
  const t = useTranslations()
  const basket = useBasket()
  const pickupDateBag = usePickupDateBag()
  const pickupDateContainer = usePickupDateContainer()
  const deliveryDateContainer = useDeliveryDateContainer()
  const { setPickupDateBag, setPickupDateContainer, setDeliveryDateContainer } = useDateStoreActions()

  const cartHasBag = basket?.basketModel.cart.some((item) => item.path.includes('isekk'))
  const cartHasContainer = basket?.basketModel.cart.some((item) => item.path.includes('container'))
  const TODAY_DATE = toOsloDateString(new Date())

  useEffect(() => {
    // Validate that the dates are set and not in the past and set them to today if they are
    if (cartHasBag && isBeforeTodayInOslo(pickupDateBag, TODAY_DATE)) {
      setPickupDateBag(TODAY_DATE)
    }
    if (cartHasContainer && isBeforeTodayInOslo(pickupDateContainer, TODAY_DATE)) {
      setPickupDateContainer(TODAY_DATE)
    }
    if (cartHasContainer && isBeforeTodayInOslo(deliveryDateContainer, TODAY_DATE)) {
      setDeliveryDateContainer(TODAY_DATE)
    }
  }, [
    TODAY_DATE,
    cartHasBag,
    cartHasContainer,
    deliveryDateContainer,
    pickupDateBag,
    pickupDateContainer,
    setDeliveryDateContainer,
    setPickupDateBag,
    setPickupDateContainer,
  ])

  useEffect(() => {
    const parsedDeliveryDateContainer = parseStoredDate(deliveryDateContainer)
    const parsedPickupDateContainer = parseStoredDate(pickupDateContainer)

    if (
      parsedDeliveryDateContainer &&
      parsedPickupDateContainer &&
      parsedDeliveryDateContainer > parsedPickupDateContainer
    ) {
      setIsInErrorState(true)
    } else {
      setIsInErrorState(false)
    }
  }, [deliveryDateContainer, pickupDateContainer, setIsInErrorState])

  return (
    <>
      {cartHasContainer && (
        <>
          <DateInput
            date={parseStoredDate(deliveryDateContainer)}
            label={t('Deliver_date_container')}
            onChange={(date?: Date) => {
              if (date) {
                setDeliveryDateContainer(toOsloDateString(date))
              }
            }}
          />
          <DateInput
            date={parseStoredDate(pickupDateContainer)}
            label={t('Pickup_date_container')}
            minDate={parseStoredDate(deliveryDateContainer)}
            onChange={(date?: Date) => {
              if (date) {
                setPickupDateContainer(toOsloDateString(date))
              }
            }}
          />
        </>
      )}
      {cartHasBag && (
        <DateInput
          date={parseStoredDate(pickupDateBag)}
          label={t('Pickup_date_bags')}
          onChange={(date?: Date) => {
            if (date) {
              setPickupDateBag(toOsloDateString(date))
            }
          }}
        />
      )}
    </>
  )
}
